import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { FadeUp } from "../components/Motion";
import PageMeta from "../components/PageMeta";
import BeforeAfterSlider from "../components/BeforeAfterSlider";

const hasTransformation = (p) =>
  p && typeof p.before_image === "string" && p.before_image.trim() !== "" &&
  typeof p.after_image === "string" && p.after_image.trim() !== "";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/projects").then((r) => setItems(r.data)); }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);
  const transformations = filtered.filter(hasTransformation);

  return (
    <div data-testid="projects-page" className="pt-32 pb-32">
      <PageMeta
        title="Portfolio — Landscape Projects in Edmonton | NorthCrest Landscaping"
        description="Selected residential and commercial landscape projects across Edmonton, Alberta. Paver patios, fire features, retaining walls, outdoor kitchens, and full backyard builds."
        path="/projects"
      />
      <section className="px-6 md:px-12 lg:px-16 mb-16">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">Portfolio</div>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-none max-w-4xl">
            Selected <span className="italic text-[#D8D6D0]">work.</span>
          </h1>
        </FadeUp>

        <div className="mt-12 flex gap-3 flex-wrap">
          {[
            { k: "all", l: "All" },
            { k: "residential", l: "Residential" },
            { k: "commercial", l: "Commercial" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`px-5 py-2 text-xs uppercase tracking-widest border transition-all duration-300 ${
                filter === f.k ? "bg-[#F5F3EF] text-[#151515] border-[#F5F3EF]" : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/60"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </section>

      {transformations.length > 0 && (
        <section data-testid="transformations-section" className="px-6 md:px-12 lg:px-16 mb-24">
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-3">Transformations</div>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-tight max-w-3xl">
              Drag to reveal <span className="italic text-[#D8D6D0]">the difference.</span>
            </h2>
            <p className="text-[#D8D6D0]/55 mt-4 max-w-2xl text-sm md:text-base">
              Before our crew arrived and after the final stone was set. Move the slider to compare.
            </p>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {transformations.slice(0, 4).map((p) => (
              <FadeUp key={`tf-${p.id}`}>
                <div className="bg-[#0d0d0d] border border-[#D8D6D0]/10">
                  <div className="aspect-[4/3] relative">
                    <BeforeAfterSlider
                      beforeImage={p.before_image}
                      afterImage={p.after_image}
                      alt={p.title}
                    />
                  </div>
                  <div className="p-6">
                    <div className="overline text-[#D8D6D0]/55 mb-1">{p.location} · {p.category}</div>
                    <div className="font-serif-display text-2xl">{p.title}</div>
                    {p.description && (
                      <p className="text-sm text-[#D8D6D0]/60 mt-2 line-clamp-2">{p.description}</p>
                    )}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6">
          {filtered.map((p, i) => (
            <FadeUp
              key={p.id}
              delay={i * 0.05}
              className={i % 5 === 0 ? "col-span-12 md:col-span-8" :
                         i % 5 === 1 ? "col-span-12 md:col-span-4" :
                         i % 5 === 2 ? "col-span-12 md:col-span-6" :
                         i % 5 === 3 ? "col-span-12 md:col-span-6" : "col-span-12 md:col-span-12"}
            >
              <div data-testid={`project-card-${p.id}`} className="group relative overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#151515]/95 via-[#151515]/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="overline text-[#D8D6D0]/70 mb-2">{p.location} · {p.category}</div>
                  <div className="font-serif-display text-2xl md:text-4xl">{p.title}</div>
                  <p className="text-sm text-[#D8D6D0]/60 mt-3 max-w-lg">{p.description}</p>
                  {p.budget_range && (
                    <div className="mt-4 text-xs uppercase tracking-widest text-[#D8D6D0]/50">Budget: {p.budget_range}</div>
                  )}
                  {hasTransformation(p) && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-[#8C1D30]/90 text-[#F5F3EF] text-[10px] uppercase tracking-widest" data-testid={`transform-badge-${p.id}`}>
                      Before / After ↑
                    </div>
                  )}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}
